import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Proposal} from '../../../model/proposal/proposal';
import {CustomValidators} from '../../../validator/custom-validator';
import {ProposalFormBase} from '../proposal-form-base/proposal-form-base.component';
import {ReplaySubject, startWith, takeUntil} from 'rxjs';
import {UserService} from '../../../services/user/user.service';
import {UserAppState} from '../../../store/user/reducers/user.reducer';
import {Store} from '@ngrx/store';
import {BinaryData} from '../../../model/proposal/binary-data';
import {UploadLocation} from '../../../enum/upload-location';
import {UploadComponent} from '../../upload-field/upload.component';
import {UserListLoadActions} from '../../../store/user/actions/userActions';
import {selectUserId, selectUserList} from '../../../store/user/selectors/user.selectors';
import {formatDate} from '@angular/common';
import {User} from '../../../model/user';
import {ProposalInformation} from '../../../model/proposal/proposal-information';

@Component({
    selector: 'app-ad-information-form',
    templateUrl: './proposal-information-form.component.html',
    styleUrls: ['./proposal-information-form.component.scss', '../../../../styles/form-input.scss'],
    providers: [{ provide: ProposalFormBase, useExisting: ProposalInformationFormComponent }],
    standalone: false
})
export class ProposalInformationFormComponent extends ProposalFormBase implements OnInit, AfterViewInit{

  @Input() isEditRoute: boolean;

  @Input() proposalPreviewInformation: ProposalInformation;

  @ViewChild(UploadComponent) uploadComponent: UploadComponent;

  @Input() set selectedLineOfBusinessNumber(lineOfBusinessNumber) {
    //If the form is used for editing, avoid disabling the toggle, otherwise it won't re-enable
    if(!this.isEditRoute){
      if (lineOfBusinessNumber == null || lineOfBusinessNumber == '') {
        this.formGroup.controls.privateProposal.patchValue(false);
        this.formGroup.controls.privateProposal.disable();
      } else {
        this.userStore.dispatch(UserListLoadActions.loadUsersByLOB({lobNumber: lineOfBusinessNumber}));
        this.formGroup.controls.privateProposal.enable();
      }
    }
  }

  userList$ = this.userStore.select(selectUserList);
  activeUserId = null;

  proposalInformationSearchArray: ProposalInformationSearch[] = [];
  maxNumber: number = 10;

  formGroup = this.fb.group({
    privateProposal: new FormControl<boolean>(false),
    deadline: new FormControl<string>(null, [Validators.required, CustomValidators.dateInFuture]),
    proposalName: new FormControl<string>(''),
    classificationTerm: new FormControl<string>(''),
    otherInformation: new FormControl<string>(''),
    imageUploads: new FormControl<BinaryData[]>([]),
    usersAllowedToView: this.fb.array<FormGroup<MemberForm>>([]),
  });
  UploadLocation = UploadLocation;

  constructor(private readonly fb: FormBuilder,
              public userService: UserService,
              private readonly userStore: Store<UserAppState>) {
    super();
  }

  setupProposalIsPrivate() {
    this.formGroup.controls.privateProposal.valueChanges
      .pipe(startWith(this.formGroup.value.privateProposal),
        takeUntil(this.destroy$))
      .subscribe(value => {
        if (value) {
          this.addMember();
          this.formGroup.controls.usersAllowedToView.enable();
        } else {
          if (this.formGroup.value.usersAllowedToView) {
            this.formGroup.value.usersAllowedToView.forEach((index) => {
                this.deleteMember(index);
              }
            );
          }

          this.formGroup.controls.usersAllowedToView.disable();
        }
      });
    this.formGroup.controls.privateProposal.updateValueAndValidity();
  }

  addMember() {
    const newFormGroup = this.fb.group<MemberForm>({
      id: new FormControl<string>('')
    });
    this.formGroup.controls.usersAllowedToView.push(newFormGroup);

    let search = new ProposalInformationSearch();

    this.userList$.pipe(takeUntil(this.destroy$)).subscribe(userList => {
      let userListWithoutActive = userList.filter(user => user.id !== this.activeUserId);
      search.filteredOptionsUser.next(userListWithoutActive.slice());
      search.filterCtrlUser.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.filterCurrencies(search.filterCtrlUser.value, userListWithoutActive, search.filteredOptionsUser);
        });

      this.proposalInformationSearchArray.push(search);
    });

  }

  deleteMember(i) {
    this.formGroup.controls.usersAllowedToView.removeAt(i);
    this.proposalInformationSearchArray.splice(i);
  }

  ngOnInit(): void {
    this.userStore.select(selectUserId).subscribe(userId => this.activeUserId = userId);
    this.setupProposalIsPrivate();
  }

  ngAfterViewInit(): void {
    if (this.proposalPreviewInformation != null) {
      this.formGroup.controls.proposalName.patchValue(this.proposalPreviewInformation.proposalName);
      if (this.proposalPreviewInformation.deadline.getFullYear() !== 1970) {
        this.formGroup.controls.deadline.patchValue(formatDate(new Date(this.proposalPreviewInformation.deadline), 'yyyy-MM-dd', 'en'));
      }
      if (this.proposalPreviewInformation.usersAllowedToView != null) {
        if (this.proposalPreviewInformation.usersAllowedToView.length > 0) {
          this.formGroup.controls.privateProposal.patchValue(true);
        }
        this.formGroup.controls.usersAllowedToView.patchValue(this.proposalPreviewInformation.usersAllowedToView.map(stringId => {
          return {id: stringId};
        }));
      }
      this.formGroup.controls.classificationTerm.patchValue(this.proposalPreviewInformation.classificationTerm);
      this.formGroup.controls.otherInformation.patchValue(this.proposalPreviewInformation.otherInformation);
    }
  }

  updateProposal(proposal: Proposal): void {
    proposal.proposalInformation.usersAllowedToView = [];
    if (this.formGroup.value.usersAllowedToView) {
      this.formGroup.value.usersAllowedToView.forEach(user => proposal.proposalInformation.usersAllowedToView.push(user.id));
    }
    proposal.proposalInformation.deadline = new Date(this.formGroup.value.deadline);
    proposal.proposalInformation.proposalName = this.formGroup.value.proposalName;
    proposal.proposalInformation.classificationTerm = this.formGroup.value.classificationTerm;
    proposal.proposalInformation.otherInformation = this.formGroup.value.otherInformation;
    proposal.proposalInformation.privateProposal = this.formGroup.value.privateProposal;
    (this.formGroup.value.imageUploads ?? []).forEach(file => {
      file.uploadLocation = UploadLocation.PROPOSALINFORMATION;
      proposal.proposalInformation.attachedFiles.push(file);
    });
  }

  autoFillForTransmission(proposal: Proposal): void {
    if (proposal.proposalInformation != null) {
      this.formGroup.controls.proposalName.patchValue(proposal.proposalInformation.proposalName);
      this.formGroup.controls.deadline.patchValue(formatDate(new Date(proposal.proposalInformation.deadline), 'yyyy-MM-dd', 'en'));
      if (proposal.proposalInformation.usersAllowedToView != null) {
        this.formGroup.controls.usersAllowedToView.patchValue(proposal.proposalInformation.usersAllowedToView.map(stringId => {
          return {id: stringId};
        }));
      }
      this.formGroup.controls.classificationTerm.patchValue(proposal.proposalInformation.classificationTerm);
      this.formGroup.controls.otherInformation.patchValue(proposal.proposalInformation.otherInformation);
      if (proposal.proposalInformation.attachedFiles != null) {
        this.uploadComponent?.addPreUploadedFiles(
          proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.PROPOSALINFORMATION)
        );
      }
    }
  }

  autoFillForEdit(proposal: Proposal) {
    if (proposal.proposalInformation != null) {

      //if we are using the form for editing, the load of the users must be done here
      this.userStore.dispatch(UserListLoadActions.loadUsersByLOB({lobNumber: proposal.lineOfBusiness.number}));

      this.formGroup.controls.proposalName.patchValue(proposal.proposalInformation.proposalName);
      this.formGroup.controls.deadline.patchValue(formatDate(new Date(proposal.proposalInformation.deadline), 'yyyy-MM-dd', 'en'));
      this.formGroup.controls.classificationTerm.patchValue(proposal.proposalInformation.classificationTerm);
      this.formGroup.controls.otherInformation.patchValue(proposal.proposalInformation.otherInformation);

      this.formGroup.controls.privateProposal.enable();
      this.formGroup.controls.privateProposal.patchValue(proposal.proposalInformation.privateProposal);

      if (proposal.proposalInformation.usersAllowedToView != null) {
        this.formGroup.controls.usersAllowedToView.patchValue(proposal.proposalInformation.usersAllowedToView.map(stringId => {
          return {id: stringId};
        }));
      }

      if (proposal.proposalInformation.attachedFiles != null) {
        this.uploadComponent?.addPreUploadedFiles(
          proposal.proposalInformation.attachedFiles.filter(file => file.uploadLocation == UploadLocation.PROPOSALINFORMATION)
        );
      }
    }
  }

  attachFiles(imageUploads: BinaryData[]): void {
    this.formGroup.patchValue({
      imageUploads: imageUploads
    });
  }

  filterCurrencies(search: string, options: User[], filtered) {

    if (!options) {
      return;
    }
    if (!search) {
      filtered.next(options.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    filtered.next(
      options.filter(user => this.toUserString(user).toLowerCase().indexOf(search) > -1)
    );
  }

  private toUserString(user: User): string {
    return user.company?.name + ' ' + user.firstName + ' ' + user.lastName;
  }

  reset() {
    this.formGroup.reset();
    this.formGroup.markAsPristine();
    this.formGroup.markAsUntouched();
  }

}

interface MemberForm {
  id: FormControl<string>;
}

class ProposalInformationSearch {
  public filterCtrlUser: FormControl<string> = new FormControl<string>('');

  public filteredOptionsUser: ReplaySubject<User[]> = new ReplaySubject<User[]>(1);

}
